import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import AuthShell from "../../components/AuthShell/AuthShell";
import TextButton from "../../components/primitives/TextButton";
import TextField from "../../components/primitives/TextField";
import { ApiError } from "../../services/api";
import { useAuth } from "../../services/AuthContext";
import "./auth.css";

const looksLikeEmail = (s) => /^\S+@\S+\.\S+$/.test(String(s ?? "").trim());

function MagicLoginPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { consumeMagicLink, requestMagicLink } = useAuth();
    const token = params.get("token");

    const [status, setStatus] = useState(token ? "loading" : "missing");
    const [serverErr, setServerErr] = useState(null);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                await consumeMagicLink(token);
                if (!cancelled) {
                    setStatus("ok");
                    setTimeout(
                        () => !cancelled && navigate("/dashboard", { replace: true }),
                        700
                    );
                }
            } catch (err) {
                if (cancelled) return;
                setStatus("invalid");
                setServerErr(
                    err instanceof ApiError && err.code === "invalid_token"
                        ? "This sign-in link is invalid, expired, or already used."
                        : err?.message || "Sign-in failed."
                );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, consumeMagicLink, navigate]);

    const requestNew = async (e) => {
        e?.preventDefault?.();
        if (!looksLikeEmail(email)) return;
        setBusy(true);
        try {
            await requestMagicLink(email.trim());
            setSent(true);
        } catch {
            setSent(true);
        } finally {
            setBusy(false);
        }
    };

    if (status === "loading") {
        return (
            <AuthShell
                eyebrow="Console / Magic sign-in"
                title="Signing you in…"
                lede="One moment."
            >
                <div className="auth-check">
                    <div className="auth-check__icon auth-check__icon--spin" aria-hidden="true">
                        <span className="auth-spinner" />
                    </div>
                </div>
            </AuthShell>
        );
    }

    if (status === "ok") {
        return (
            <AuthShell
                eyebrow="Console / Magic sign-in"
                title="You're in."
                lede="Redirecting to your dashboard."
            >
                <div className="auth-check auth-check--success">
                    <div className="auth-check__icon auth-check__icon--success" aria-hidden="true">
                        <CheckCircle2 size={20} />
                    </div>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell
            eyebrow="Console / Magic sign-in"
            title="Link invalid or expired."
            lede={serverErr || "Request a new sign-in link below."}
            footer={
                <>
                    <Link to="/login" className="auth-shell-link auth-shell-link--accent">
                        ← Back to sign-in
                    </Link>
                </>
            }
        >
            <div className="auth-check auth-check--error">
                <div className="auth-check__icon auth-check__icon--error" aria-hidden="true">
                    <AlertTriangle size={20} />
                </div>
            </div>

            <form className="auth-form" onSubmit={requestNew} noValidate>
                <TextField
                    type="email"
                    label="Email"
                    required
                    value={email}
                    onChange={setEmail}
                    placeholder="you@company.com"
                    autoComplete="email"
                    hint="We'll send a fresh sign-in link."
                />
                <TextButton
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={busy}
                    disabled={sent}
                >
                    {sent ? "Sent — check your inbox" : "Send a new link"}
                </TextButton>
            </form>
        </AuthShell>
    );
}

export default MagicLoginPage;
